import React, { useContext, useState, useEffect, useRef } from "react";
import {
    WizardView,
    useWizardPage,
    useSyncedField,
    useRuntimeField,
    FinishPage,
} from "../../components/Wizard";
import classNames from "classnames";
import Select from "react-select";
import { valueFromAST } from "graphql";
import { range } from "lodash";
import { param } from "jquery";
import StarekContext from "../../context/StarekContext";
import {
    FALLBACK_DEVICE_FLAGS,
    computeGpioType,
    decodeGpioType,
    computeUartType,
    normalizeRuntimeSignals,
    manifestDefaultType,
    computePhysicalSignalColors,
    compareSignalKeys,
} from "../device/gpioTypeUtils";
import {
    ANALYZER_KINDS,
    ANALYZER_KIND_FIELDS,
    defaultAnalyzerConfig,
} from "../device/analyzerSchema";

// Mirrors backend Device.SIGNAL_COLORS (00117-starek-backend-v2/src/models/
// Device.ts) - fallback only, until GET /_types has loaded. Cycled across
// every channel in manifest order (not reset per bus) so a profile's
// channels are visually distinct on a graph, matching deviceBind's/
// defaultSignalCosmetics' own convention on the backend.
export const FALLBACK_SIGNAL_COLORS = [
    "333333", "8F6305", "EF2D2D", "F79B2A", "F2F251",
    "20AC6B", "007BFF", "9751F2", "A9B0BC", "FCFCFC",
];

// A native <option>'s inline `style.color` is ignored by most browsers' OS-
// rendered dropdown popups, so the Source dropdown's color dot needs a real
// custom dropdown (react-select) instead of a styled native <select> - its
// menu renders in the page itself, where formatOptionLabel's colored <span>
// actually shows.
const formatSourceOption = (opt) => (
    <div className="d-flex align-items-center gap-2">
        <span
            className="d-inline-block rounded-circle flex-shrink-0"
            style={{
                width: 10,
                height: 10,
                backgroundColor: `#${opt.color || "000000"}`,
            }}
        />
        <span>
            {opt.label}
            {opt.disabled ? " (already in use)" : ""}
        </span>
    </div>
);
const selectMenuPortalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

// Manifest-declared parameters that describe an INTERPRETATION, not the
// physical bus - I2C addressing is inherently per-reading/per-device (a bus
// can have multiple devices on it), never a hardware fact of the bus
// itself, so these are excluded from the physical Signals page and instead
// belong on an Interpretation's analyzer (see InterpretationsPage).
const INTERPRETATION_ONLY_PARAM_NAMES = ["i2c_address", "i2c_register"];

// Builds the same shape as DeviceProfile.configuration ({model, signals:
// {gpio,i2c,uart,virt}, interpretations, attributes}) but sourced from a
// bound device's own current effective state - signal peripheral-init
// params/types (Device.overrides.signals / reported), interpretations, and
// attributes, overlaid onto the model's manifest for structure (which ports
// exist, their address/index/params). Used both to pre-fill the "Configure
// Device" wizard when editing a device directly, and to snapshot a device's
// current setup into a new Device Profile - in both cases "effective" means
// whatever the device actually has right now, regardless of whether it came
// from a profile or manual edits.
export function buildDeviceEffectiveConfiguration(device, availableModels, deviceFlags) {
    const modelCode = device?.device?.model;
    const modelManifest = (availableModels || []).find((m) => m.code === modelCode);
    if (!modelManifest) return null;

    const flags = deviceFlags || FALLBACK_DEVICE_FLAGS;
    const currentTypes = new Map(
        normalizeRuntimeSignals(device?.device?.runtime?.signals).map((s) => [
            s.name,
            s.type,
        ])
    );

    // Physical signals only - hardware facts and peripheral-init parameters.
    // No cosmetics (friendly_name/color/inverted/auxillary) here - those are
    // Interpretation concerns, captured via effectiveInterpretations below.
    const rawSignalsByBus = modelManifest.downlink?.signals || {};
    const signals = {};
    Object.keys(rawSignalsByBus).forEach((bus) => {
        const entries = rawSignalsByBus[bus];
        const rows = Array.isArray(entries)
            ? [...entries].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0))
            : [entries];

        signals[bus] = rows.map((entry) => {
            const address = entry.address || entry.name;
            const channel = {
                name: entry.name,
                address: entry.address,
                ...(entry.parameters || []).reduce((acc, param) => {
                    acc[param.name] = param.default;
                    return acc;
                }, {}),
            };
            if (bus === "gpio") {
                const effectiveType = currentTypes.has(address)
                    ? currentTypes.get(address)
                    : entry.type;
                Object.assign(
                    channel,
                    decodeGpioType(effectiveType, flags, entry.parameters)
                );
                channel.type = effectiveType;
            }
            return channel;
        });
    });

    // Effective (merged) interpretations and attributes - "whatever this
    // device actually has right now", same philosophy as signals above.
    // Attributes are filtered to PROFILE_ATTRIBUTE_FIELDS's settable set
    // (declared further down this file) - that same filter is what keeps
    // DEVICE_ONLY_ATTRIBUTES and computed extras (e.g. `location`, added
    // onto runtime.attributes by ShadowDeviceFieldResolver.ts) out of a
    // profile that has no business templating them.
    const effectiveInterpretations = device?.device?.runtime?.interpretations || [];
    const runtimeAttributes = device?.device?.runtime?.attributes || {};
    const settableAttributeNames = new Set(
        PROFILE_ATTRIBUTE_FIELDS.map((f) => f.name)
    );
    const attributes = Object.fromEntries(
        Object.entries(runtimeAttributes).filter(([name]) =>
            settableAttributeNames.has(name)
        )
    );

    return {
        model: {
            name: modelManifest.name,
            image: modelManifest.image,
            code: modelManifest.code,
        },
        signals,
        interpretations: effectiveInterpretations,
        attributes,
    };
}

// Flattens the {gpio,i2c,uart,virt} grouped shape into one list - mirrors
// the backend's flattenProfileSignalEntries/Cosmetics exactly, just
// client-side, for the "configure this device directly" wizard flow which
// writes straight to the device instead of going through a Device Profile.
function flattenManifestSignals(signalsByBus) {
    if (!signalsByBus) return [];
    const entries = [];
    for (const bus of ["gpio", "i2c", "uart", "virt"]) {
        const busEntries = signalsByBus[bus];
        if (Array.isArray(busEntries)) entries.push(...busEntries);
    }
    return entries;
}

export function flattenManifestSignalsToTypeArray(
    signalsByBus,
    flags = FALLBACK_DEVICE_FLAGS,
) {
    return flattenManifestSignals(signalsByBus)
        .map((e) => {
            // GPIO entries in a raw manifest carry axis-default `parameters`
            // instead of a literal `type` - manifestDefaultType composes
            // those into a bitmask so a never-overridden GPIO port still
            // gets a real type here, not null.
            const flattened = {
                name: e.address || e.name,
                type: manifestDefaultType(e, flags),
            };
            // The analyzer is self-contained (endpoint/i2c_address/
            // i2c_register live inside it - see analyzerSchema.js / the
            // backend's flattenProfileSignalEntries, which this mirrors) -
            // only forwarded when present.
            if (e.analyzer) flattened.analyzer = e.analyzer;
            return flattened;
        })
        .filter((e) => e.name && e.type !== undefined && e.type !== null);
}

export function flattenManifestSignalsToCosmetics(signalsByBus) {
    return flattenManifestSignals(signalsByBus).reduce((acc, e) => {
        const key = e.address || e.name;
        if (!key) return acc;
        acc[key] = {
            color: e.color,
            inverted: e.inverted,
            auxillary: e.auxillary,
            friendly_name: e.friendly_name,
        };
        return acc;
    }, {});
}

// Shared REST fetch for the workspace's available device models - used
// anywhere a model manifest needs to be resolved: DeviceTypePage's own
// picker, AddDeviceConfigWizard's inputManifest pre-fill, and "Save as
// Profile"'s effective-config snapshot.
export async function fetchAvailableDeviceModels(starekContext) {
    const { scheme, host, port } = starekContext.lastUsedWorkspace?.engine || {};
    const workspace = starekContext.lastUsedWorkspace?.name;
    const authToken = starekContext.auth;
    if (!scheme || !host || !port || !workspace || !authToken) {
        return [];
    }
    const apiUrl = `${scheme}://${host}:${port}/api/v1/${workspace}/devices/available`;
    const response = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const jsonData = await response.json();
    return jsonData?.data || [];
}

export function DeviceTypePage({ inputManifest, emittedManifest, locked }) {
    const starekContext = useContext(StarekContext);
    const { scheme, host, port } =
        starekContext.lastUsedWorkspace?.engine || {};
    const workspace = starekContext.lastUsedWorkspace?.name;
    const authToken = starekContext.auth;

    const [availableModels, setAvailableModels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const hasFetchedRef = useRef(false);

    const [selectedDevice, setSelectedDevice] = useRuntimeField(
        "selected_device",
        null
    );

    const [modelInfo, setModelInfo] = useSyncedField("model", null);
    const [, setSignals] = useSyncedField("signals", {});

    const deviceModels = availableModels || [];

    useEffect(() => {
        if (hasFetchedRef.current) {
            return;
        }

        const fetchAvailableDevices = async () => {
            if (!scheme || !host || !port || !workspace || !authToken) {
                console.warn("Missing required context for API call");
                setIsLoading(false);
                return;
            }

            hasFetchedRef.current = true;
            const apiUrl = `${scheme}://${host}:${port}/api/v1/${workspace}/devices/available`;

            try {
                setIsLoading(true);
                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const jsonData = await response.json();
                const models = jsonData?.data || [];

                setAvailableModels(models);

                setError(null);
            } catch (err) {
                console.error("Failed to fetch available device models:", err);
                setError(err.message);
                starekContext.showError?.(
                    `Failed to load available device models: ${err.message}`
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchAvailableDevices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheme, host, port, workspace, authToken]);

    const handleModelSelect = (model) => {
        setSelectedDevice(model);
        console.log("Selected model:", model);
        setModelInfo({
            name: model.name,
            image: model.image,
            code: model.code,
        });
        setSignals({}); 
    };

    useEffect(() => {
        if (
            modelInfo?.code &&
            availableModels.length > 0 &&
            !selectedDevice
        ) {
            const savedModel = availableModels.find(
                (m) => m.code === modelInfo.code
            );
            console.log("Found Saved Model:", savedModel);
            if (savedModel) {
                setSelectedDevice(savedModel);
            }
        }
    }, [modelInfo, availableModels, selectedDevice, setSelectedDevice]);

    return (
        <div>
            <h3 className="mb-3">Device Type</h3>
            <p className="text-muted mb-4">
                {locked
                    ? "This device's model is fixed by its hardware and can't be changed here."
                    : "Choose the model of device that this profile can define. The model number is printed on the package of the device."}
            </p>
            {isLoading && (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                >
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">
                            Loading available device models...
                        </span>
                    </div>
                </div>
            )}

            {!isLoading && error && deviceModels.length === 0 && (
                <div
                    className="d-flex justify-content-center align-items-center flex-column"
                    style={{ minHeight: "400px" }}
                >
                    <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                    <p className="text-danger">
                        Failed to load available device models
                    </p>
                    <p className="text-muted">{error}</p>
                </div>
            )}

            {!isLoading && deviceModels.length > 0 && (
                <div className="mb-3">
                    <label className="fw-bold mb-3">
                        Model <span className="text-danger">*</span>
                    </label>
                    <div className="row g-3">
                        {deviceModels.map((model) => {
                          const isSelected = selectedDevice?.code === model.code;
                          const disabled = locked && !isSelected;
                          return (
                            <div key={model.code} className="col-auto">
                                <div
                                    className={classNames(
                                        "card text-center p-3",
                                        disabled ? "cursor-not-allowed" : "cursor-pointer",
                                        {
                                            "border-primary": isSelected,
                                            "border-3": isSelected,
                                            "bg-light": isSelected,
                                        }
                                    )}
                                    style={{
                                        width: "200px",
                                        cursor: disabled ? "not-allowed" : "pointer",
                                        opacity: disabled ? 0.4 : 1,
                                        boxShadow: isSelected
                                            ? "0 4px 6px rgba(0,0,0,0.3)"
                                            : "none",
                                    }}
                                    onClick={() =>
                                        !disabled && handleModelSelect(model)
                                    }
                                >
                                    <div
                                        className="mb-2"
                                        style={{
                                            height: "140px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <img
                                            src={
                                                model.image ||
                                                "/path/to/default-device-image.png"
                                            }
                                            alt={model.name}
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = "none";
                                                e.target.nextSibling.style.display =
                                                    "flex";
                                            }}
                                        />
                                        <i
                                            className={classNames(
                                                "fas fa-microchip fa-4x",
                                                {
                                                    "text-primary":
                                                        selectedDevice ===
                                                        model.code,
                                                    "text-muted":
                                                        selectedDevice !==
                                                        model.code,
                                                }
                                            )}
                                            style={{
                                                display: model.image
                                                    ? "none"
                                                    : "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                            aria-hidden="true"
                                        ></i>
                                    </div>
                                    <div className="mb-1">
                                        <div
                                            className={classNames("fw-bold", {
                                                "text-primary":
                                                    selectedDevice ===
                                                    model.code,
                                            })}
                                        >
                                            {model.code.toUpperCase()}
                                        </div>
                                        <div
                                            className={classNames("small", {
                                                "text-primary":
                                                    selectedDevice ===
                                                    model.code,
                                                "text-muted":
                                                    selectedDevice !==
                                                    model.code,
                                            })}
                                        >
                                            {model.name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                          );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export function SignalsPage({ inputManifest, emittedManifest, onNextRequested }) {
    const { runtimeVariables } = useWizardPage();
    const starekContext = useContext(StarekContext);
    const deviceFlags = starekContext?.types?.device?.flags || FALLBACK_DEVICE_FLAGS;

    const [signals, setSignals] = useSyncedField("signals", {});
    // Which channel/row is currently being reviewed per bus, and which rows
    // have been looked at at least once - lives in runtime state (not
    // page-local) so the wizard's onNextRequested validation can see it.
    const [visitedRows, setVisitedRows] = useRuntimeField(
        "visited_signal_rows",
        {}
    );

    // Read shared runtime state (set by DeviceTypePage). DynamoDB's Map (M)
    // type doesn't guarantee key order on read (unlike List/L, which does),
    // so bus display order needs an explicit hint (signal_bus_order) rather
    // than trusting Object.keys() on the signals map - and each bus's own
    // channel array is re-sorted by its own `index` field too, as a direct,
    // self-describing safeguard rather than trusting array order alone.
    const selectedDevice = runtimeVariables?.selected_device;
    const rawSignalsByBus = selectedDevice?.downlink?.signals || {};
    const signalsByBus = {};
    Object.keys(rawSignalsByBus).forEach((bus) => {
        const entries = rawSignalsByBus[bus];
        signalsByBus[bus] = Array.isArray(entries)
            ? [...entries].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0))
            : entries;
    });
    const busOrderHint = selectedDevice?.downlink?.signal_bus_order;
    const availableBuses = Array.isArray(busOrderHint)
        ? busOrderHint.filter((bus) => signalsByBus[bus])
        : Object.keys(signalsByBus);

    // Page-local state (only needed for UI in this page)
    const [selectedBus, setSelectedBus] = useState(availableBuses[0] || null);
    const [selectedRowIndex, setSelectedRowIndex] = useState({});

    // Initialize signals with default values from device definition. Every
    // bus is an array of one-or-more channel descriptors - a bus with a
    // single physical instance (uart, i2c) is just an array of length 1,
    // there's no separate "flat object" shape to special-case.
    useEffect(() => {
        if (Object.keys(signals).length > 0) {
            // Editing already-configured data - it already has real values,
            // so mark every row visited instead of forcing a re-review of
            // channels that were already set up correctly.
            if (Object.keys(visitedRows).length === 0) {
                const allVisited = {};
                Object.keys(signals).forEach((bus) => {
                    const rows = Array.isArray(signals[bus]) ? signals[bus] : [];
                    allVisited[bus] = Object.fromEntries(
                        rows.map((_, i) => [i, true])
                    );
                });
                setVisitedRows(allVisited);
            }
            return;
        }

        if (signalsByBus && Object.keys(signalsByBus).length > 0) {
            const initialSignals = {};
            const initialVisited = {};

            availableBuses.forEach((busType) => {
                const busData = signalsByBus[busType];
                const rows = Array.isArray(busData) ? busData : [busData];

                initialSignals[busType] = rows.map((signal) => {
                    // Physical signals are hardware facts only - name/address
                    // never change, and the only configurable thing here is
                    // peripheral-init parameters (GPIO axis, UART baud/parity/
                    // etc, whatever the manifest declares). Aliasing, color,
                    // inverted/auxiliary are business context and belong on an
                    // Interpretation instead (see InterpretationsPage) - never
                    // seeded here.
                    const channel = {
                        name: signal.name,
                        address: signal.address,
                        ...signal.parameters?.reduce((acc, param) => {
                            acc[param.name] = param.default;
                            return acc;
                        }, {}),
                    };
                    // type is only meaningful for gpio - it's the port's own
                    // SIGNAL_TYPE_BITMAP value. Seed from the manifest's own
                    // fixed type first (some models declare one directly,
                    // e.g. starek-nwm-sim's AI/DI/AO/DO ports with no
                    // configurable dropdowns), then let computeGpioType
                    // derive/override it from discreteOrProportional etc.
                    // when those exist.
                    if (busType === "gpio") {
                        channel.type = signal.type;
                        channel.type = computeGpioType(channel, deviceFlags);
                    } else if (busType === "uart") {
                        channel.type = signal.type;
                        channel.type = computeUartType(channel, signal.parameters, deviceFlags);
                    }
                    return channel;
                });

                // Only the row the user lands on by default counts as
                // reviewed - the rest need an explicit look via the dropdown.
                if (rows.length > 1) initialVisited[busType] = { 0: true };
            });

            setSignals(initialSignals);
            setVisitedRows(initialVisited);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevice?.code]);

    // Prefers the live, editable signals[bus] array once it's been seeded -
    // signalsByBus (the device-model manifest) never changes, but signals[bus]
    // can grow beyond it once a logical signal (see analyzerSchema.js) is
    // added, and that added row needs to actually be reachable via the row
    // picker below, not just present in state.
    const rowCountForBus = (bus) => {
        if (Array.isArray(signals[bus])) return signals[bus].length;
        const busManifest = signalsByBus[bus];
        if (Array.isArray(busManifest)) return busManifest.length;
        return busManifest ? 1 : 0;
    };
    const isMultiRow = (bus) => rowCountForBus(bus) > 1;

    const markVisited = (bus, rowIndex) => {
        setVisitedRows({
            ...visitedRows,
            [bus]: { ...(visitedRows[bus] || {}), [rowIndex]: true },
        });
    };

    // Landing on a multi-row bus counts as reviewing whichever row is shown
    // by default, even if the dropdown itself is never touched.
    useEffect(() => {
        if (!selectedBus) return;
        const idx = selectedRowIndex[selectedBus] ?? 0;
        markVisited(selectedBus, idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBus]);

    const handleRowSelect = (bus, rowIndex) => {
        setSelectedRowIndex({ ...selectedRowIndex, [bus]: rowIndex });
        markVisited(bus, rowIndex);
    };

    const handleChannelFieldChange = (bus, rowIndex, field, value) => {
        const updated = [...(signals[bus] || [])];
        const merged = { ...updated[rowIndex], [field]: value };
        if (bus === "gpio") {
            merged.type = computeGpioType(merged, deviceFlags);
        } else if (bus === "uart") {
            const busManifest = signalsByBus[bus];
            const manifestRows = Array.isArray(busManifest) ? busManifest : [busManifest];
            merged.type = computeUartType(merged, manifestRows[rowIndex]?.parameters, deviceFlags);
        }
        updated[rowIndex] = merged;
        setSignals({ ...signals, [bus]: updated });
    };

    const busLabels = { gpio: "GPIO", uart: "UART", i2c: "I2C", virt: "VIRT" };

    // Renders one manifest-declared parameter as a labeled, stacked field -
    // 1D buses (a single row) and 2D buses (channel picker above) both use
    // this for whichever row is currently selected.
    const renderManifestField = (param, channel, onFieldChange) => {
        const currentValue = channel[param.name] ?? param.default;
        const isReadonly = !!param.readonly;
        const isDropdown = param.type === "select";
        return (
            <div className="mb-3" key={param.name}>
                <label className="form-label d-block">{param.name}</label>
                {isReadonly ? (
                    <div className="form-control bg-light">{currentValue}</div>
                ) : isDropdown ? (
                    <select
                        className="form-select"
                        value={currentValue}
                        onChange={(e) =>
                            onFieldChange(param.name, e.target.value)
                        }
                    >
                        {(param.range || []).map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        type={param.type === "number" ? "number" : "text"}
                        className="form-control"
                        value={currentValue}
                        onChange={(e) =>
                            onFieldChange(param.name, e.target.value)
                        }
                    />
                )}
            </div>
        );
    };


    // Physical signals only - hardware facts (name/address never change) and
    // peripheral-init parameters (GPIO axis, UART baud/parity/etc, whatever
    // the manifest declares for this bus). Aliasing, color, inverted/
    // auxiliary and adding a brand new derived signal are all Interpretation
    // concerns now (see InterpretationsPage) - this page never authors them.
    const renderBusForm = (bus) => {
        const rows = signals[bus] || [];
        if (rows.length === 0) {
            return <p className="text-muted">No parameters available</p>;
        }

        const multi = isMultiRow(bus);
        const rowIndex = multi ? selectedRowIndex[bus] ?? 0 : 0;
        const channel = rows[rowIndex];
        if (!channel) return null;

        const busManifest = signalsByBus[bus];
        const manifestRows = Array.isArray(busManifest)
            ? busManifest
            : [busManifest];
        // i2c_address/i2c_register are which DEVICE on the bus to talk to -
        // inherently a per-reading concern (I2C is multi-device), not a
        // hardware fact of the bus itself, so they belong on the
        // Interpretation's analyzer (see InterpretationsPage), never here -
        // same reasoning that already keeps them off the device sidebar's
        // read-only Signals tab.
        const manifestParams = (manifestRows[rowIndex]?.parameters || []).filter(
            (param) => !INTERPRETATION_ONLY_PARAM_NAMES.includes(param.name),
        );
        const onFieldChange = (field, value) =>
            handleChannelFieldChange(bus, rowIndex, field, value);

        const rowLabel = (i) =>
            rows[i]?.name ||
            manifestRows[i]?.name ||
            manifestRows[i]?.address ||
            `Signal ${i + 1}`;
        const visitedForBus = visitedRows[bus] || {};
        const unvisitedCount = rows.length - Object.keys(visitedForBus).length;

        return (
            <div className="card">
                <div className="card-body">
                    {multi && (
                        <>
                            <label className="form-label d-block">
                                Signal
                            </label>
                            <select
                                className="form-select mb-3"
                                value={rowIndex}
                                onChange={(e) =>
                                    handleRowSelect(bus, Number(e.target.value))
                                }
                            >
                                {rows.map((_, i) => (
                                    <option key={i} value={i}>
                                        {rowLabel(i)}
                                        {visitedForBus[i]
                                            ? ""
                                            : " (not reviewed)"}
                                    </option>
                                ))}
                            </select>
                            {unvisitedCount > 0 && (
                                <div className="alert alert-warning py-2 px-3 small mb-3">
                                    {unvisitedCount} of {rows.length}{" "}
                                    {busLabels[bus] || bus} signals haven't
                                    been reviewed yet - select each one from
                                    the dropdown above to check its
                                    configuration before finishing.
                                </div>
                            )}
                        </>
                    )}

                    {manifestParams.length > 0 ? (
                        manifestParams.map((param) =>
                            renderManifestField(param, channel, onFieldChange)
                        )
                    ) : (
                        <p className="text-muted small mb-0">
                            No configurable parameters for this signal.
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <h3 className="mb-3">Signals</h3>
            <p className="text-muted mb-4">
                Setup the Signals for this Device Profile
            </p>

            <div className="row">
                {/* Left side - Bus selection */}
                <div className="col-md-4">
                    <div className="d-flex flex-column gap-3">
                        {availableBuses.map((bus) => {
                            const visitedForBus = visitedRows[bus] || {};
                            const total = rowCountForBus(bus);
                            const needsReview =
                                isMultiRow(bus) &&
                                Object.keys(visitedForBus).length < total;
                            return (
                                <div
                                    key={bus}
                                    className={classNames(
                                        "card p-3 cursor-pointer",
                                        {
                                            "border-success bg-light":
                                                selectedBus === bus,
                                            "border-2": selectedBus === bus,
                                        }
                                    )}
                                    onClick={() => setSelectedBus(bus)}
                                >
                                    <div className="d-flex align-items-center">
                                        <label className="mb-0 fw-bold flex-grow-1">
                                            {busLabels[bus] || bus}
                                            {needsReview && (
                                                <span className="badge bg-warning text-dark ms-2">
                                                    !
                                                </span>
                                            )}
                                        </label>
                                        <i className="fas fa-chevron-right text-muted"></i>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right side - Bus configuration */}
                <div className="col-md-8">
                    {selectedBus ? (
                        renderBusForm(selectedBus)
                    ) : (
                        <div className="card">
                            <div className="card-body text-center text-muted">
                                <p>Select a bus type to configure</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Business-context signals authored on the profile itself - alias, which
// physical signal it reads (source), analyzer, inverted/auxiliary. Mirrors
// the device sidebar's Interpretation tab exactly (same wire shape), just
// scoped to the profile being built - applied to a device at
// assignDeviceProfile time the same way signals/cosmetics already are.
function InterpretationsPage() {
    const { runtimeVariables } = useWizardPage();
    const starekContext = useContext(StarekContext);
    const defaultSignalColors =
        starekContext?.types?.device?.defaultSignalColors || FALLBACK_SIGNAL_COLORS;
    const [interpretations, setInterpretations] = useSyncedField(
        "interpretations",
        [],
    );

    const selectedDevice = runtimeVariables?.selected_device;
    const rawSignalsByBus = selectedDevice?.downlink?.signals || {};
    const physicalEndpointOptions = [];
    // The manifest's own i2c_address (e.g. "0x40") is a sane starting point
    // for a NEW interpretation reading that bus - the bus-level parameter
    // moved off the physical Signals page (see INTERPRETATION_ONLY_PARAM_NAMES
    // above), but its declared default is still worth surfacing here, where
    // per-device addressing actually belongs.
    const manifestI2cAddressByEndpoint = {};
    Object.values(rawSignalsByBus).forEach((entries) => {
        (Array.isArray(entries) ? entries : [entries]).forEach((entry) => {
            const address = entry?.address || entry?.name;
            if (address) physicalEndpointOptions.push(address);
            const i2cParam = entry?.parameters?.find((p) => p.name === "i2c_address");
            if (address && i2cParam?.default !== undefined) {
                manifestI2cAddressByEndpoint[address] = Number(i2cParam.default) || 0;
            }
        });
    });
    // Object.values() here just reflects whatever order the manifest's own
    // bus keys happen to be in - not the deterministic bus/port order the
    // device sidebar's Signals tab (and LiveView) use, so without this sort
    // the Source dropdown's option order drifted out of sync with every
    // other listing of the same signals.
    physicalEndpointOptions.sort(compareSignalKeys);

    // The same computed BBROYGBVGW color a physical signal shows in the
    // LiveView graph and the device sidebar (see
    // gpioTypeUtils.computePhysicalSignalColors) - used here for the Source
    // dropdown's color dot and as the default an interpretation inherits
    // from its source when it has no explicit color of its own.
    const physicalSignalColors = computePhysicalSignalColors(
        physicalEndpointOptions,
        defaultSignalColors,
    );

    const [newName, setNewName] = useState("");
    const [newSource, setNewSource] = useState("");
    const [newAnalyzer, setNewAnalyzer] = useState(
        defaultAnalyzerConfig(ANALYZER_KINDS[0].value),
    );
    const [newI2cAddress, setNewI2cAddress] = useState("");
    const [newI2cRegister, setNewI2cRegister] = useState("");
    const [newInverted, setNewInverted] = useState(false);
    const [newAuxillary, setNewAuxillary] = useState(false);
    const [newColor, setNewColor] = useState(defaultSignalColors[0]);

    // Same cap the backend enforces (updateDeviceInterpretations) - a plain
    // /uart source has no addressing, so two interpretations there can
    // corrupt each other on the wire. I2C is unrestricted (addressed via
    // i2c_address/i2c_register). RS485 will get its own bus type later
    // with real multi-device addressing, not subject to this cap.
    const uartTaken = interpretations.some((i) => i.source === "/uart");

    const resetForm = () => {
        setNewName("");
        setNewSource("");
        setNewAnalyzer(defaultAnalyzerConfig(ANALYZER_KINDS[0].value));
        setNewI2cAddress("");
        setNewI2cRegister("");
        setNewInverted(false);
        setNewAuxillary(false);
        // Not resetting color here - clearing newSource above already
        // re-triggers the useEffect keyed on it, which recomputes newColor
        // from physicalEndpointOptions[0]'s own color.
    };

    // Suggests the selected source's own computed color for a brand new
    // interpretation - an interpretation inherits its source signal's color
    // by default, unless the user overrides it here. Recomputed whenever
    // the chosen source changes (including the implicit default of
    // physicalEndpointOptions[0] before the user has touched the dropdown).
    useEffect(() => {
        const source = newSource || physicalEndpointOptions[0];
        setNewColor(physicalSignalColors[source] || defaultSignalColors[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSource]);

    const handleAdd = () => {
        const trimmed = newName.trim();
        const source = newSource || physicalEndpointOptions[0];
        if (
            !trimmed ||
            !source ||
            physicalEndpointOptions.includes(trimmed) ||
            interpretations.some((i) => i.name === trimmed) ||
            (source === "/uart" && uartTaken)
        )
            return;
        const entry = {
            name: trimmed,
            source,
            analyzer: newAnalyzer,
            ...(source.startsWith("/i2c")
                ? {
                      i2c_address:
                          Number(newI2cAddress) ||
                          manifestI2cAddressByEndpoint[source] ||
                          0,
                      i2c_register: Number(newI2cRegister) || 0,
                  }
                : {}),
            inverted: newInverted,
            auxillary: newAuxillary,
            // Only persist a color when it actually differs from the
            // source's own computed color - leaving it unset is what makes
            // "inherit from source by default" a live behavior (see
            // DeviceGraph.jsx's fallback to the source's color) rather than
            // a frozen snapshot of it at creation time.
            ...(newColor !== physicalSignalColors[source] ? { color: newColor } : {}),
        };
        setInterpretations([...interpretations, entry]);
        resetForm();
    };

    const handleDelete = (name) => {
        setInterpretations(interpretations.filter((i) => i.name !== name));
    };

    return (
        <div className="p-2">
            <p className="text-muted small mb-3">
                Business-context signals layered on top of this profile's
                physical signals - an alias, where it reads from, and how to
                decode it. Applied to a device the same way signals/cosmetics
                already are when this profile is assigned.
            </p>
            {interpretations.length === 0 && (
                <p className="text-muted small">
                    No interpretations configured yet.
                </p>
            )}
            {interpretations.map((interp) => (
                <div key={interp.name} className="card mb-2">
                    <div className="card-body d-flex align-items-center py-2">
                        <span
                            className="d-inline-block rounded-circle me-2 flex-shrink-0"
                            style={{
                                width: 12,
                                height: 12,
                                backgroundColor: `#${
                                    interp.color ||
                                    physicalSignalColors[interp.source] ||
                                    "0080ff"
                                }`,
                            }}
                        />
                        <div className="flex-grow-1">
                            <div>{interp.name}</div>
                            <div className="text-muted small">
                                via {interp.source} · {interp.analyzer?.kind}
                                {interp.inverted && " · Inverted"}
                                {interp.auxillary && " · Auxiliary"}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(interp.name)}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            ))}

            <div className="card mt-3">
                <div className="card-body">
                    <div className="fw-medium mb-3">+ New Interpretation</div>

                    <div className="mb-2">
                        <label className="form-label small">Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="e.g. soil sensor"
                        />
                        {newName.trim() &&
                            (physicalEndpointOptions.includes(newName.trim()) ||
                                interpretations.some(
                                    (i) => i.name === newName.trim(),
                                )) && (
                                <div className="text-danger small mt-1">
                                    That name is already in use - pick a
                                    different one.
                                </div>
                            )}
                    </div>

                    <div className="mb-2">
                        <label className="form-label small">
                            Source (which physical signal to read from)
                        </label>
                        <Select
                            options={physicalEndpointOptions.map((ep) => ({
                                value: ep,
                                label: ep,
                                color: physicalSignalColors[ep],
                                disabled: ep === "/uart" && uartTaken,
                            }))}
                            value={(() => {
                                const ep = newSource || physicalEndpointOptions[0] || "";
                                return { value: ep, label: ep, color: physicalSignalColors[ep] };
                            })()}
                            onChange={(opt) => setNewSource(opt ? opt.value : "")}
                            isOptionDisabled={(opt) => opt.disabled}
                            formatOptionLabel={formatSourceOption}
                            menuPortalTarget={document.body}
                            styles={selectMenuPortalStyles}
                        />
                    </div>

                    {(newSource || physicalEndpointOptions[0] || "").startsWith(
                        "/i2c",
                    ) && (
                        <div className="row mb-2">
                            <div className="col">
                                <label className="form-label small">
                                    I2C address
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder={String(
                                        manifestI2cAddressByEndpoint[
                                            newSource || physicalEndpointOptions[0]
                                        ] ?? "",
                                    )}
                                    value={newI2cAddress}
                                    onChange={(e) =>
                                        setNewI2cAddress(e.target.value)
                                    }
                                />
                            </div>
                            <div className="col">
                                <label className="form-label small">
                                    I2C register
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={newI2cRegister}
                                    onChange={(e) =>
                                        setNewI2cRegister(e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    )}

                    <div className="mb-2">
                        <label className="form-label small">
                            Packet format (how to decode what comes back)
                        </label>
                        <select
                            className="form-select mb-2"
                            value={newAnalyzer.kind}
                            onChange={(e) =>
                                setNewAnalyzer(
                                    defaultAnalyzerConfig(e.target.value),
                                )
                            }
                        >
                            {ANALYZER_KINDS.map((k) => (
                                <option key={k.value} value={k.value}>
                                    {k.label}
                                </option>
                            ))}
                        </select>
                        <div className="row">
                            {(ANALYZER_KIND_FIELDS[newAnalyzer.kind] || []).map(
                                (f) => (
                                    <div className="col-6 mb-2" key={f.name}>
                                        <label className="form-label small">
                                            {f.label}
                                        </label>
                                        {f.type === "select" ? (
                                            <select
                                                className="form-select"
                                                value={
                                                    newAnalyzer[f.name] ??
                                                    f.default
                                                }
                                                onChange={(e) =>
                                                    setNewAnalyzer({
                                                        ...newAnalyzer,
                                                        [f.name]:
                                                            e.target.value,
                                                    })
                                                }
                                            >
                                                {f.options.map((opt) => (
                                                    <option
                                                        key={opt.value}
                                                        value={opt.value}
                                                    >
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type={
                                                    f.type === "number"
                                                        ? "number"
                                                        : "text"
                                                }
                                                className="form-control"
                                                value={newAnalyzer[f.name] ?? ""}
                                                onChange={(e) =>
                                                    setNewAnalyzer({
                                                        ...newAnalyzer,
                                                        [f.name]:
                                                            f.type === "number"
                                                                ? Number(
                                                                      e.target
                                                                          .value,
                                                                  )
                                                                : e.target
                                                                      .value,
                                                    })
                                                }
                                            />
                                        )}
                                    </div>
                                ),
                            )}
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label small d-block">
                            Color
                        </label>
                        <input
                            type="color"
                            className="form-control form-control-color"
                            value={`#${newColor}`}
                            onChange={(e) =>
                                setNewColor(e.target.value.replace("#", ""))
                            }
                        />
                    </div>

                    <div className="d-flex gap-3 mb-3">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="interp-inverted"
                                checked={newInverted}
                                onChange={(e) => setNewInverted(e.target.checked)}
                            />
                            <label
                                className="form-check-label small"
                                htmlFor="interp-inverted"
                            >
                                Inverted
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="interp-auxillary"
                                checked={newAuxillary}
                                onChange={(e) =>
                                    setNewAuxillary(e.target.checked)
                                }
                            />
                            <label
                                className="form-check-label small"
                                htmlFor="interp-auxillary"
                            >
                                Auxiliary
                            </label>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        disabled={
                            !newName.trim() ||
                            physicalEndpointOptions.includes(newName.trim()) ||
                            interpretations.some(
                                (i) => i.name === newName.trim(),
                            )
                        }
                        onClick={handleAdd}
                    >
                        Add Interpretation
                    </button>
                </div>
            </div>
        </div>
    );
}

// Firmware's authoritative attribute set (main/include/app_attributes.h's
// kAttributes[]), minus every Settable::DEVICE_ONLY entry (display_on,
// current_job_code, current_job_name - a profile has no business templating
// something the device manages locally; deviceSetAttribute already rejects
// backend writes to these, mirrored client-side by DeviceAttributesPanel.jsx's
// own DEVICE_ONLY_ATTRIBUTES list). There's no manifest-declared schema for
// attributes in Dynamo (checked device.available_models directly - no such
// key exists), so - same precedent as that DEVICE_ONLY_ATTRIBUTES list -
// this hardcodes the field set by hand from firmware's own table.
const PROFILE_ATTRIBUTE_FIELDS = [
    { name: "probe_interval", label: "Probe interval", type: "number", unit: "ms" },
    { name: "heartbeat_interval", label: "Heartbeat interval", type: "number", unit: "ms" },
    { name: "under_maintenance", label: "Under maintenance", type: "boolean" },
    { name: "show_job_schedule", label: "Show job schedule", type: "boolean" },
    { name: "timezone", label: "Timezone offset", type: "number", unit: "min" },
    { name: "gps_lat", label: "GPS latitude", type: "text" },
    { name: "gps_lon", label: "GPS longitude", type: "text" },
    { name: "battery_warning", label: "Battery warning threshold", type: "number", unit: "%" },
];

// Device-level config a profile can template - unlike signals/interpretations
// this is a small fixed field set, not a dynamic add/delete list: each field
// starts unchecked (not part of this profile at all) and only becomes part
// of configuration.attributes once enabled, so a profile can template just
// the couple of fields it actually cares about.
function AttributesPage() {
    const [attributes, setAttributes] = useSyncedField("attributes", {});

    const toggleField = (name, checked) => {
        if (checked) {
            const field = PROFILE_ATTRIBUTE_FIELDS.find((f) => f.name === name);
            setAttributes({
                ...attributes,
                [name]: field.type === "boolean" ? false : "",
            });
        } else {
            const next = { ...attributes };
            delete next[name];
            setAttributes(next);
        }
    };

    const updateValue = (name, value) => {
        setAttributes({ ...attributes, [name]: value });
    };

    return (
        <div className="p-2">
            <p className="text-muted small mb-3">
                Device-level settings this profile should set - a device only
                picks up a field once you enable it below; anything left
                unchecked is untouched by this profile.
            </p>
            {PROFILE_ATTRIBUTE_FIELDS.map((field) => {
                const enabled = Object.prototype.hasOwnProperty.call(
                    attributes,
                    field.name,
                );
                return (
                    <div className="card mb-2" key={field.name}>
                        <div className="card-body py-2">
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`attr-enable-${field.name}`}
                                    checked={enabled}
                                    onChange={(e) =>
                                        toggleField(field.name, e.target.checked)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor={`attr-enable-${field.name}`}
                                >
                                    {field.label}
                                    {field.unit ? ` (${field.unit})` : ""}
                                </label>
                            </div>
                            {enabled &&
                                (field.type === "boolean" ? (
                                    <div className="form-check form-switch">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={!!attributes[field.name]}
                                            onChange={(e) =>
                                                updateValue(
                                                    field.name,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    </div>
                                ) : (
                                    <input
                                        type={field.type === "number" ? "number" : "text"}
                                        className="form-control"
                                        value={attributes[field.name] ?? ""}
                                        onChange={(e) =>
                                            updateValue(
                                                field.name,
                                                field.type === "number"
                                                    ? Number(e.target.value)
                                                    : e.target.value,
                                            )
                                        }
                                    />
                                ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// Blocks "Next"/finish until every channel of every multi-row bus (e.g. GPIO's
// 4 ports) has been reviewed at least once via SignalsPage's dropdown - a
// user who only ever looks at the first channel would otherwise silently
// leave the rest on whatever default the manifest declared.
export function validateSignalsReviewed(e) {
    // signals is a synced field (useSyncedField), so it lives in
    // emittedManifest; visited_signal_rows is a runtime field
    // (useRuntimeField) and lives in runtimeVariables - these are two
    // separate objects on the event, not one shared bag.
    const signals = e.detail.emittedManifest?.signals || {};
    const visited = e.detail.runtimeVariables?.visited_signal_rows || {};
    for (const bus of Object.keys(signals)) {
        const rows = signals[bus];
        if (Array.isArray(rows) && rows.length > 1) {
            const visitedCount = Object.keys(visited[bus] || {}).length;
            if (visitedCount < rows.length) {
                e.detail.prevented = true;
                return;
            }
        }
    }
}

export default function AddDeviceProfileWizard(props) {
    const handleFinish = async (emittedManifest) => {
        if (props.onFinish) {
            await props.onFinish(emittedManifest);
        }
    };

    return (
        <WizardView
            onClose={props.onClose}
            onFinish={handleFinish}
            emittedManifest={props.initialConfig}
            title="Device Configurator"
            width="1100px"
            height="600px"
            showFinishPage={false}
        >
            <WizardView.Page
                title="Device Type"
                icon="microchip"
                onNextRequested={(e) => {
                    const selectedDevice =
                        e.detail.runtimeVariables?.selected_device;
                    /** validate moves here */
                    if (!selectedDevice) {
                        e.detail.prevented = true;
                    }
                }}
            >
                <DeviceTypePage />
            </WizardView.Page>

            <WizardView.Page
                title="Signals"
                icon="arrow-down"
                onNextRequested={validateSignalsReviewed}
            >
                <SignalsPage />
            </WizardView.Page>

            <WizardView.Page title="Interpretations" icon="tags">
                <InterpretationsPage />
            </WizardView.Page>

            <WizardView.Page title="Attributes" icon="sliders-h">
                <AttributesPage />
            </WizardView.Page>

            <WizardView.Page title="Review" icon="check-circle">
                <FinishPage />
            </WizardView.Page>
        </WizardView>
    );
}

